import { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Card } from './ui/card';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    name: 'Beata Wielgoszewski',
    role: 'Psych Registered Nurse',
    quote: 'From my very first interaction, it was clear that Digitive Health operates with a high level of professionalism and care. Every step was handled thoughtfully, with clear communication and dependable follow-through. The consistency, transparency, and overall support truly distinguish Digitive Health from other agencies.',
    image: 'https://customer-assets.emergentagent.com/job_digitive-rebuild/artifacts/2mafiad4_Beata.png',
  },
  {
    name: 'Brandi Koster',
    role: 'Certified Sterile Tech',
    quote: 'My experience with Digitive Health has been very positive and well-coordinated. Communication was clear, timelines were respected, and the overall process felt organized and professional. I would confidently recommend Digitive Health to healthcare professionals seeking a trustworthy and efficient agency.',
    image: 'https://customer-assets.emergentagent.com/job_digitive-rebuild/artifacts/eoa0b043_Brandi.png',
  },
  {
    name: 'Merline Samson',
    role: 'Healthcare Professional',
    quote: 'My experience with Digitive Health has been outstanding from start to finish. The onboarding process was exceptionally smooth, well-coordinated, and faster than expected. The teamwork, professionalism, and clear communication truly set Digitive Health apart.',
    image: 'https://customer-assets.emergentagent.com/job_digitive-rebuild/artifacts/evdi689p_Merline.png',
  },
  {
    name: 'Nelda David',
    role: 'Certified Nursing Assistant',
    quote: 'Working with Digitive Health was a very positive experience. The onboarding process was quick, clear, and hassle-free, and I felt supported throughout each step. I would definitely recommend Digitive Health to other healthcare professionals looking for a reliable agency.',
    image: 'https://customer-assets.emergentagent.com/job_digitive-rebuild/artifacts/uhfqvhhy_Nelda.png',
  },
  {
    name: 'Jordan Blackwell',
    role: 'Registered Nurse',
    quote: 'I would like to highly recommend Digitive Health for their fast, smooth, and well-organized onboarding process. The entire experience was seamless, with clear communication and strong support throughout each step, making the process efficient and stress-free.',
    image: 'https://customer-assets.emergentagent.com/job_digitive-rebuild/artifacts/zdr5dbon_Jordan.png',
  },
];

export const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [titleRef, titleVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const activeTestimonial = testimonials[activeIndex];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="section-padding bg-secondary relative texture-overlay"
    >
      <div className="container-width relative z-10">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-12 animate-on-scroll ${titleVisible ? 'visible' : ''}`}
        >
          <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-4">
            Testimonials
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-primary">
            Real Stories. Real Impact
          </h2>
        </div>

        {/* Testimonial Content */}
        <div
          ref={contentRef}
          className={`animate-on-scroll ${contentVisible ? 'visible' : ''}`}
        >
          <Card className="overflow-hidden bg-white shadow-xl max-w-5xl mx-auto" data-testid="featured-testimonial">
            <div className="p-8 md:p-12">
              {/* Centered Photo */}
              <div className="flex justify-center mb-8">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden ring-4 ring-primary/20 ring-offset-4 shadow-lg">
                  <img
                    src={activeTestimonial.image}
                    alt={activeTestimonial.name}
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                    data-testid="testimonial-photo"
                  />
                </div>
              </div>

              {/* Quote */}
              <div className="text-center max-w-3xl mx-auto">
                <Quote className="h-10 w-10 text-primary/20 mx-auto mb-6" />
                <p className="text-lg md:text-xl text-foreground leading-relaxed mb-8">
                  "{activeTestimonial.quote}"
                </p>
                
                {/* Name and Role */}
                <div className="border-t border-border pt-6 inline-block px-8">
                  <p className="text-xl font-bold text-primary mb-1">
                    {activeTestimonial.name}
                  </p>
                  <p className="text-muted-foreground">
                    {activeTestimonial.role}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Navigation */}
          <div className="mt-8 flex flex-col items-center gap-6">
            {/* Thumbnail Row */}
            <div className="flex items-center justify-center gap-4">
              {testimonials.map((testimonial, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`relative overflow-hidden transition-all duration-300 ${
                    index === activeIndex 
                      ? 'w-14 h-14 md:w-16 md:h-16 rounded-full ring-4 ring-primary ring-offset-2 shadow-lg' 
                      : 'w-10 h-10 md:w-12 md:h-12 rounded-full opacity-50 hover:opacity-100 grayscale hover:grayscale-0'
                  }`}
                  data-testid={`testimonial-thumb-${index}`}
                >
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-full h-full object-cover object-center"
                  />
                </button>
              ))}
            </div>

            {/* Arrow Navigation */}
            <div className="flex items-center gap-4">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 rounded-full bg-white border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors shadow-sm"
                data-testid="testimonial-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-muted-foreground min-w-[60px] text-center font-medium">
                {activeIndex + 1} / {testimonials.length}
              </span>
              <button
                onClick={nextTestimonial}
                className="w-12 h-12 rounded-full bg-white border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors shadow-sm"
                data-testid="testimonial-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
